/**
 * Pure, framework-independent permission resolution for admin routes.
 * Kept separate from ProtectedRoute.tsx so it can be unit-tested without React.
 */

/**
 * Maps a URL path segment to the permission slug required for `.view` access.
 * Routes not listed here are accessible to any authenticated user (e.g. dashboard, profile).
 */
export const ROUTE_PERMISSION_MAP: Record<string, string> = {
  admissions: 'admissions.view',
  'student-sessions': 'admissions.view',
  'my-students': 'children.view',
  enquiries: 'enquiries.view',
  attendance: 'attendance.view',
  homework: 'homework.view',
  events: 'events.view',
  gallery: 'gallery.view',
  complaints: 'complaints.view',
  'progress-cards': 'progress.view',
  documents: 'documents.view',
  finance: 'dashboard.finance',
  vehicles: 'transport.view',
  routes: 'transport.view',
  'student-transport': 'transport.view',
  'staff-profiles': 'staff.view',
  invoices: 'invoices.view',
  'fee-payment-requests': 'invoices.view',
  daycares: 'daycares.view',
  transportation: 'transport.view',
  fees: 'fees.view',
  users: 'users.view',
  'academic-sessions': 'academic_sessions.view',
  'fee-structures': 'fee_structures.view',
  notifications: 'notifications.view',
  announcements: 'notifications.view',
  moments: 'moments.view',
  classes: 'classes.view',
  roles: 'rbac.view',
  cms: 'cms.view',
  'settings/branches': 'branches.view',
  'settings/schedule': 'settings.view',
  'settings/general': 'settings.view',
};

/**
 * Maps "<module>/<action>" keys to the functional permission slug required
 * to access that sub-route. Used to gate `/new`, `/edit`, `/upload`, etc.
 * independently of the base module's `.view` permission.
 */
export const ACTION_PERMISSION_MAP: Record<string, string> = {
  'admissions/new': 'admissions.create',
  'admissions/edit': 'admissions.update',
  'admissions/promote': 'promotions.create',
  'student-sessions/new': 'admissions.create',
  'student-sessions/edit': 'admissions.update',
  'enquiries/new': 'enquiries.create',
  'enquiries/edit': 'enquiries.update',
  'attendance/mark': 'attendance.mark',
  'homework/new': 'homework.create',
  'homework/edit': 'homework.create',
  'events/new': 'events.create',
  'events/edit': 'events.create',
  'gallery/upload': 'gallery.upload',
  'progress-cards/new': 'progress.create',
  'progress-cards/edit': 'progress.create',
  'documents/issue': 'documents.issue',
  'vehicles/new': 'transport.create',
  'vehicles/edit': 'transport.update',
  'routes/new': 'transport.create',
  'routes/edit': 'transport.update',
  'staff-profiles/new': 'staff.manage',
  'staff-profiles/edit': 'staff.manage',
  'invoices/new': 'invoices.create',
  'invoices/edit': 'invoices.update',
  'daycares/new': 'daycares.create',
  'daycares/edit': 'daycares.update',
  'transportation/new': 'transport.create',
  'transportation/edit': 'transport.update',
  'fees/new': 'fees.create',
  'fees/edit': 'fees.update',
  'users/new': 'users.create',
  'users/edit': 'users.update',
  'academic-sessions/new': 'academic_sessions.create',
  'academic-sessions/edit': 'academic_sessions.update',
  'fee-structures/new': 'fee_structures.create',
  'fee-structures/edit': 'fee_structures.update',
  'announcements/create': 'notifications.create',
  'announcements/edit': 'notifications.create',
  'moments/new': 'moments.create',
  'classes/new': 'classes.create',
  'classes/edit': 'classes.update',
  'settings/branches/new': 'branches.create',
  'settings/branches/edit': 'branches.update',
  'cms/new': 'cms.create',
  'cms/edit': 'cms.update',
};

export const ACTION_KEYWORDS = ['new', 'edit', 'upload', 'create', 'mark', 'issue', 'promote'];

/**
 * Resolves the permission slug required to access the given pathname.
 * Returns `null` if the route is publicly accessible to any authenticated user.
 *
 * Resolution order:
 *  1. If the last segment is an action keyword (e.g. "new", "upload"),
 *     look it up in ACTION_PERMISSION_MAP ("module/action").
 *  2. Else try two-segment key (e.g. "settings/branches").
 *  3. Else try single-segment key (e.g. "admissions").
 *  4. Else return null (no permission required).
 */
export function getRequiredPermission(pathname: string): string | null {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const last = segments[segments.length - 1];
  if (ACTION_KEYWORDS.includes(last)) {
    const modulePath =
      segments[0] === 'settings' && segments.length >= 3
        ? `${segments[0]}/${segments[1]}`
        : segments[0];
    const actionKey = `${modulePath}/${last}`;
    if (ACTION_PERMISSION_MAP[actionKey]) return ACTION_PERMISSION_MAP[actionKey];
  }

  const twoSeg = `${segments[0]}/${segments[1] ?? ''}`;
  if (ROUTE_PERMISSION_MAP[twoSeg]) return ROUTE_PERMISSION_MAP[twoSeg];
  if (ROUTE_PERMISSION_MAP[segments[0]]) return ROUTE_PERMISSION_MAP[segments[0]];
  return null;
}

